import { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { searchProductions, updateProduction } from 'services/api/production';
import { getOrders } from 'services/outside/aaro/dekont.js';
import { notify } from 'helpers/notificationHelper';

// Create a context for search functionality
const SearchContext = createContext();

// Custom hook to use the search context
export const useSearch = () => {
    return useContext(SearchContext);
};

// Helper function to check if a date is older than given hours
const isOlderThan = (date, hours) => {
    if (!date) return true; // Eğer updatedAt değeri yoksa güncellensin
    try {
        const now = new Date();
        const pastDate = new Date(date);
        const diffInMs = now - pastDate;
        const diffInHours = diffInMs / (1000 * 60 * 60);
        return diffInHours > hours;
    } catch (e) {
        console.error('Tarih formatı hatası:', e);
        return true; // Hata durumunda güvenli tarafta kalmak için true döndür
    }
};

// Function to fetch order from Aaro by DekontID
const fetchAaroOrderByDekontID = async (dekontID) => {
    try {
        console.log(`Aaro'dan ${dekontID} sipariş ID'si ile sipariş çekiliyor...`);
        const response = await getOrders({
            DekontID: dekontID,
        });

        console.log(`${dekontID} için Aaro'dan gelen yanıt:`, response?.data?.Model);
        return response?.data?.Model || [];
    } catch (error) {
        console.error(`Aaro siparişi çekilirken hata (DekontID: ${dekontID}):`, error);
        return [];
    }
};

// Provider component to wrap components that need access to search functionality
export const SearchProvider = ({ children }) => {
    // State variables for search filters
    const [searchQuery, setSearchQuery] = useState('');
    const [productionStatus, setProductionStatus] = useState([]);
    const [responsibles, setResponsibles] = useState([]);
    const [dateRange, setDateRange] = useState({});
    const [isActive, setIsActive] = useState(true);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(500);
    const [isSyncing, setIsSyncing] = useState(false);
    const [filters, setFilters] = useState({ hideDepoSiparisi: true }); // New filters state with default hideDepoSiparisi
    const queryClient = useQueryClient();

    // Build search parameters based on filter values
    const buildSearchParams = () => {
        const params = {};

        // Add search query if present
        if (searchQuery) params.searchQuery = searchQuery;

        // Add production status if present
        if (productionStatus && productionStatus.length > 0) {
            params.productionStatus = productionStatus.map((status) => status._id);
        }

        // Add responsibles if present
        if (responsibles && responsibles.length > 0) {
            params.responsibles = responsibles.map((resp) => resp._id);
        }

        // Add date range if present
        if (dateRange && (dateRange.startDate || dateRange.endDate)) {
            params.dateRange = dateRange;
        }

        // Add active status
        params.isActive = isActive;

        // Add pagination
        params.page = page;
        params.limit = limit;

        return params;
    };

    // Query for searching productions
    const searchQueryResult = useQuery({
        queryKey: ['searchProductions', searchQuery, productionStatus, responsibles, dateRange, isActive, page, limit],
        queryFn: () => searchProductions(buildSearchParams()).then((rp) => rp.data),
        enabled: true,
        keepPreviousData: true,
    });

    // Apply filters to the data (frontend filtering)
    const filteredData = useMemo(() => {
        const rawData = searchQueryResult.data?.result || [];

        if (!rawData || rawData.length === 0) return rawData;
        if (!filters || Object.keys(filters).length === 0) return rawData;

        console.log('Applying filters:', filters);
        console.log('Raw data length:', rawData.length);

        const filtered = rawData.filter((item) => {
            // Filter out "Depo Siparişi" tags
            if (filters.hideDepoSiparisi) {
                const hasDepoSiparisi = item.tags?.some((tag) => tag.name === 'Depo Siparişi');
                if (hasDepoSiparisi) {
                    console.log('Filtering out Depo Siparişi:', item.aaroCustomerName);
                    return false;
                }
            }

            // Filter out closed items (aaroFields.Kapali === true)
            if (filters.hideKapali) {
                if (item.aaroFields?.Kapali === true) {
                    console.log('Filtering out Kapalı:', item.aaroCustomerName);
                    return false;
                }
            }

            // Filter by approval status (aaroFields.OnayDurum)
            if (filters.onayDurum && filters.onayDurum.length > 0) {
                const itemOnayDurum = item.aaroFields?.OnayDurum;
                if (!filters.onayDurum.includes(itemOnayDurum)) {
                    console.log('Filtering out by OnayDurum:', item.aaroCustomerName, 'OnayDurum:', itemOnayDurum);
                    return false;
                }
            }

            return true;
        });

        console.log('Filtered data length:', filtered.length);
        return filtered;
    }, [searchQueryResult.data?.result, filters]);

    // Create filtered data object with same structure as original
    const filteredDataObject = useMemo(() => {
        if (!searchQueryResult.data) return null;

        return {
            ...searchQueryResult.data,
            result: filteredData,
            totalCount: filteredData.length, // Update total count for filtered data
        };
    }, [searchQueryResult.data, filteredData]);

    // Mutation for updating productions
    const updateProductionMutation = useMutation({
        mutationFn: (data) => updateProduction(data.id, data.updates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['searchProductions'] });
        },
    });

    // Function to add delay between operations to prevent API overload
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    // Auto-sync function to update productions older than 6 hours
    const autoSyncProductions = async () => {
        if (isSyncing) return;

        // Veri yapısını kontrol et ve üretimleri al
        let productions = [];

        if (searchQueryResult.data?.result) {
            if (Array.isArray(searchQueryResult.data.result)) {
                productions = searchQueryResult.data.result;
            } else if (
                searchQueryResult.data.result.productions &&
                Array.isArray(searchQueryResult.data.result.productions)
            ) {
                productions = searchQueryResult.data.result.productions;
            }
        }

        if (!productions.length) return;

        setIsSyncing(true);

        // Güncellenecek üretimleri filtrele (6 saat)
        const outdatedProductions = productions.filter((production) => {
            return production.aaroOrder && isOlderThan(production.updatedAt, 6);
        });

        if (outdatedProductions.length === 0) {
            setIsSyncing(false);
            return;
        }

        console.log(`${outdatedProductions.length} adet 6 saatten eski üretim bulundu. Güncelleniyor...`);

        let updatedCount = 0;

        for (const production of outdatedProductions) {
            try {
                // Fetch latest data from Aaro using DekontID
                const aaroOrders = await fetchAaroOrderByDekontID(production.aaroOrder);

                if (aaroOrders.length === 0) continue;

                // Doğrudan DekontID ile aradığımız için ilk sonuç bizim istediğimiz sipariştir
                const matchingOrder = aaroOrders[0];
                console.log(`Güncelleme için kullanılacak veri (${production.aaroOrder}):`, matchingOrder);

                if (!matchingOrder) continue;

                // Güncelleme işlemini gerçekleştir
                console.log(`${production._id} üretimi için güncelleme yapılıyor...`);
                const updateResult = await updateProductionMutation.mutateAsync({
                    id: production._id,
                    updates: {
                        aaroFields: matchingOrder,
                    },
                });

                console.log(`Güncelleme sonucu (${production._id}):`, updateResult);

                updatedCount++;
                console.log(`Toplam ${updatedCount} üretim güncellendi.`);
                await delay(300); // Add delay to prevent API overload
            } catch (error) {
                console.error(`Üretim güncellenirken hata (${production._id}):`, error);
            }
        }

        if (updatedCount > 0) {
            notify(true, `${updatedCount} üretim otomatik olarak Aaro ile senkronize edildi.`);
            queryClient.invalidateQueries({ queryKey: ['searchProductions'] });
        }

        setIsSyncing(false);
    };

    // Effect to trigger auto-sync when productions are loaded
    useEffect(() => {
        if (searchQueryResult.isSuccess && !searchQueryResult.isLoading && searchQueryResult.data) {
            autoSyncProductions();
        }
    }, [searchQueryResult.isSuccess, searchQueryResult.dataUpdatedAt]);

    // Reset filters function
    const resetFilters = () => {
        setSearchQuery('');
        setProductionStatus([]);
        setResponsibles([]);
        setDateRange({});
        setIsActive(true);
        setPage(1);
    };

    // Update functions for each filter
    const updateSearchQuery = (newQuery) => {
        setSearchQuery(newQuery);
    };
    const updateResponsibles = (newResponsible) => {
        setResponsibles(newResponsible);
    };
    const updateDateRange = (newDateRange) => {
        setDateRange(newDateRange);
    };
    const updateProductionStatus = (newProductionStatus) => {
        setProductionStatus(newProductionStatus);
    };
    const updatePage = (page) => {
        setPage(page);
    };
    const updateLimit = (limit) => {
        setLimit(limit);
    };
    const updateIsActive = (active) => {
        setIsActive(active);
    };

    // Update filters function
    const updateFilters = (newFilters) => {
        setFilters(newFilters);
        setPage(1); // Reset to first page when filters change
    };

    // Provide all search functionality and state to children
    return (
        <SearchContext.Provider
            value={{
                searchProductionQuery: searchQuery, // For backward compatibility
                searchQuery,
                productionStatus,
                responsibles,
                dateRange,
                isActive,
                page,
                limit,
                data: filteredDataObject,
                queryInfo: searchQueryResult,
                isSyncing,
                updateSearchProductionQuery: updateSearchQuery, // For backward compatibility
                updateSearchQuery,
                updateProductionStatus,
                updateResponsibles,
                updateDateRange,
                updateIsActive,
                updatePage,
                updateLimit,
                resetFilters,
                manualSync: autoSyncProductions,
                filters,
                updateFilters,
            }}
        >
            {children}
        </SearchContext.Provider>
    );
};
